import { Signal, computed } from '@angular/core';
import { Store } from '@ngrx/store';

import { AppRoutes } from '@gfe/app.paths';
import { TransformerInterface } from '@gfe/core/models/entities/entity-visualization-interface';
import {
  ROUTE_LOCK_OPTIONS,
  ROUTE_VALIDATION_OPTIONS
} from '@gfe/core/models/entities/implementations/user-route-v1';
import { CustomTransformerResponse } from '@gfe/core/models/transformers.model';
import { MAX_BULK_PATCH_ITEMS } from '@gfe/core/services/entity-modification/entity-modification.service';
import { ICONS } from '@gfe/core/services/icons/icons.service';
import {
  selectEditAvailability,
  selectIsPlanInViewHeld,
  selectPlanEditSession
} from '@gfe/core/store/plan-edit/plan-edit-session.selectors';
import { selectPlan } from '@gfe/core/store/plan/plan.selectors';
import { isPlanEditBlocked } from '@gfe/core/utils/plan-edit.utils';
import { isNil } from '@gfe/core/utils/shared-utils';
import { planEditToolboxItem } from '@gfe/features/components/plan/edit/plan-edit-toolbox-item';
import {
  ToolboxDropdownButton,
  ToolboxSection
} from '@gfe/shared/components/toolbox/toolbox.component';

import { EntityComponent } from './entity.component';

const VALIDATED_ROUTES_LOCKED_KEY = 'features.data.toolbox.validated_routes_locked';

/**
 * Overwrites the lock of the selected routes. Shown on the route entity inside a plan only:
 * locks steer re-optimization, and the batch route is plan-scoped.
 *
 * The count that matters is what the action would write to, which under the select-all banner
 * is the whole matching set rather than the fetched page.
 */
function buildRouteLockDropdown<T>(
  entityComponent: EntityComponent<T>,
  heldReason: string | null
): ToolboxDropdownButton {
  const targetCount = entityComponent.bulkTargetCount();
  const overLimit = targetCount > MAX_BULK_PATCH_ITEMS;
  const sharedLockCfg = entityComponent.selectedRoutesLockCfg();
  const hasValidatedRoute = entityComponent.selectionHasValidatedRoute();

  return {
    id: 'route-lock',
    icon: ICONS.lock,
    disabled:
      targetCount === 0 ||
      overLimit ||
      entityComponent.bulkWriteInFlight() ||
      heldReason !== null ||
      hasValidatedRoute,
    tooltip:
      heldReason ??
      (overLimit
        ? entityComponent.translate.instant('features.data.toolbox.lock_routes_limit', {
            max: MAX_BULK_PATCH_ITEMS
          })
        : entityComponent.translate.instant(
            hasValidatedRoute
              ? VALIDATED_ROUTES_LOCKED_KEY
              : 'features.data.toolbox.lock_routes'
          )),
    items: ROUTE_LOCK_OPTIONS.map((option) => ({
      label: entityComponent.translate.instant(option.labelKey),
      iconClass: option.icon,
      // A check only where the whole selection already agrees; a mixed one claims nothing.
      isSelected: option.value === sharedLockCfg,
      action: () => entityComponent.applyRouteLock(option.value)
    }))
  };
}

/** Sets the validation state of the selected routes, under the same reach rules as the lock. */
function buildRouteValidationDropdown<T>(
  entityComponent: EntityComponent<T>,
  heldReason: string | null
): ToolboxDropdownButton {
  const targetCount = entityComponent.bulkTargetCount();
  const overLimit = targetCount > MAX_BULK_PATCH_ITEMS;
  const sharedState = entityComponent.selectedRoutesValidationState();
  const blocker = entityComponent.selectionValidationBlocker();

  return {
    id: 'route-validation',
    icon: ICONS.checkCircle,
    disabled:
      targetCount === 0 ||
      overLimit ||
      entityComponent.bulkWriteInFlight() ||
      heldReason !== null,
    tooltip:
      heldReason ??
      (overLimit
        ? entityComponent.translate.instant('features.data.toolbox.validate_routes_limit', {
            max: MAX_BULK_PATCH_ITEMS
          })
        : entityComponent.translate.instant('features.data.toolbox.validate_routes')),
    items: ROUTE_VALIDATION_OPTIONS.map((option) => {
      const label = entityComponent.translate.instant(option.labelKey);
      // Only validating is blocked; unvalidating must stay reachable.
      const blocked = option.value === 'Validated' && blocker !== null;
      // The options are verbs, so the current state is disabled as a no-op rather than
      // highlighted, which would read as the action already picked.
      return {
        label: blocked ? `${label} — ${entityComponent.translate.instant(blocker)}` : label,
        iconClass: option.icon,
        disabled: blocked || option.value === sharedState,
        action: () => entityComponent.applyRouteValidation(option.value)
      };
    })
  };
}

export function buildToolboxSections<T>(
  entityComponent: EntityComponent<T>,
  store: Store,
  customTransformers: Signal<CustomTransformerResponse[]>
): Signal<ToolboxSection[]> {
  const plan = store.selectSignal(selectPlan);
  const planHeld = store.selectSignal(selectIsPlanInViewHeld);
  const editAvailability = store.selectSignal(selectEditAvailability);
  const editSession = store.selectSignal(selectPlanEditSession);

  return computed<ToolboxSection[]>(() => {
    entityComponent.currentLang();
    const currentPlan = plan();
    const inPlan = !isNil(currentPlan);
    // A mid-optimization or already-optimized plan refuses writes, so the entry point has to
    // refuse first: otherwise the user learns it only after filling in a form or preparing a
    // CSV.
    const editBlocked = inPlan && isPlanEditBlocked(currentPlan);
    const heldReason = planHeld()
      ? entityComponent.translate.instant('features.plan.edit.held.write_disabled')
      : null;
    const hasSelection = entityComponent.hasSelectionSignal();
    const hasValidatedRoute =
      entityComponent.entityType() === 'route' &&
      entityComponent.selectionHasValidatedRoute();
    const transformerButtons = entityComponent
      .currentEntityTransformers()
      .map((transformer: TransformerInterface) => {
        const rawTransformerName = transformer.transformerDisplayLabel();
        const translatedTransformerName =
          entityComponent.entityLabelsTranslationService.translateCsvTransformerName(
            rawTransformerName
          );

        return {
          label: translatedTransformerName,
          action: () => {
            entityComponent.router.navigate([AppRoutes.data.dataUpload], {
              relativeTo: entityComponent.activatedRoute,
              queryParams: { transformer: rawTransformerName }
            });
          },
          iconClass: ICONS.list
        };
      });
    const customGenerationButtons = customTransformers().map((transformer) => {
      return {
        label: transformer.name,
        action: () => {
          entityComponent.router.navigate(
            [AppRoutes.data.customGeneration.buildPath(transformer.id)],
            {
              relativeTo: entityComponent.activatedRoute
            }
          );
        },
        iconClass: ICONS.wrench
      };
    });

    const exportTransformerButtons = entityComponent
      .currentCsvEntityModelNames()
      .map((modelName: string) => {
        const translatedTransformerName =
          entityComponent.entityLabelsTranslationService.translateEntityModelName(
            modelName
          );

        return {
          label: translatedTransformerName,
          action: () => {
            entityComponent.downloadData('csv', modelName);
          },
          iconClass: ICONS.list
        };
      });

    // The edition context is the import-from-global picker, rendered in a modal on the plan
    // overview route: its CSV items navigate to the `upload` child relative to the injected
    // route, and that route has none, so offering them would strand the user. Same reason
    // `canUpload` refuses a dropped file there.
    const canAddData = entityComponent.contextType() !== 'edition';
    const addData = {
      id: 'add',
      icon: ICONS.plus,
      tooltip:
        heldReason ?? entityComponent.translate.instant('features.data.toolbox.add_data'),
      disabled: editBlocked || heldReason !== null,
      items: [
        {
          label: entityComponent.translate.instant('common.form'),
          action: () => entityComponent.newEntityFromForm(),
          iconClass: ICONS.bracketsCurly
        },
        ...transformerButtons,
        // Custom generation is a submit surface of its own, with reference lookups that
        // still resolve against global data; offering it here would write outside the plan.
        ...(inPlan ? [] : customGenerationButtons)
      ]
    };
    const canLockRoutes = entityComponent.entityType() === 'route' && inPlan;
    const planEdit =
      currentPlan && canLockRoutes && canAddData
        ? planEditToolboxItem({
            availability: editAvailability(),
            planParameterId: currentPlan.parameter?.id ?? null,
            sessionMode: editSession().mode,
            parameters: entityComponent.planEditParameters(),
            translate: (key, params) => entityComponent.translate.instant(key, params),
            edit: (paramId) => entityComponent.startPlanEdit(paramId)
          })
        : null;
    const visuButtonIcon = entityComponent.isVisuEnabled() ? ICONS.eyeSlash : ICONS.eye;
    const visuButtonTooltipTranslationKey = entityComponent.isVisuEnabled()
      ? 'features.data.toolbox.disable_visu'
      : 'features.data.toolbox.enable_visu';
    const visuButtonTooltip = entityComponent.translate.instant(
      visuButtonTooltipTranslationKey
    );

    const visuButton = entityComponent.isVisuAllowed()
      ? [
          {
            id: 'visu',
            icon: visuButtonIcon,
            disabled: entityComponent.isPopupOpen(),
            tooltip: visuButtonTooltip,
            callback: () => {
              entityComponent.isVisuEnabled.set(!entityComponent.isVisuEnabled());
            }
          }
        ]
      : [];

    return [
      {
        id: 'default actions',
        items: [
          ...(planEdit ? [planEdit] : []),
          ...(canAddData ? [addData] : []),
          ...visuButton,
          {
            id: 'export',
            icon: ICONS.download,
            disabled: entityComponent.exportState().isExporting,
            tooltip: entityComponent.translate.instant('features.data.toolbox.export_data'),
            items: [
              {
                label: 'JSON',
                action: () => entityComponent.downloadData('json'),
                iconClass: ICONS.file
              },
              ...(entityComponent.showTableCsvExport()
                ? [
                    {
                      label: entityComponent.translate.instant(
                        'features.data.toolbox.export_csv_table'
                      ),
                      action: () => entityComponent.downloadTableDisplayedData(),
                      iconClass: ICONS.rows
                    }
                  ]
                : []),
              ...exportTransformerButtons
            ]
          }
        ]
      },
      // Actions available table items are selected
      {
        id: 'bulk-actions',
        items: [
          ...(canLockRoutes
            ? [
                buildRouteLockDropdown(entityComponent, heldReason),
                buildRouteValidationDropdown(entityComponent, heldReason)
              ]
            : []),
          {
            id: 'delete',
            icon: ICONS.trash,
            tooltip:
              heldReason ??
              entityComponent.translate.instant(
                hasValidatedRoute ? VALIDATED_ROUTES_LOCKED_KEY : 'common.delete'
              ),
            disabled: !hasSelection || heldReason !== null || hasValidatedRoute,
            callback: () => {
              if (hasSelection) {
                entityComponent.deleteEntitiesModal.set({
                  ...entityComponent.deleteEntitiesModal(),
                  isOpen: true
                });
              }
            }
          }
        ]
      }
    ];
  });
}
